import { useState, useEffect } from 'react';
import axios from 'axios';
import { useCompany } from '../context/CompanyContext';
import { Plus, Loader2, BookOpen, MoreHorizontal, Pencil, Trash2, Globe, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ExportImportButtons } from '../components/ExportImportButtons';
import { useExportImport } from '../hooks/useExportImport';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { name: '', total_invested: '', country: '', note_due_date: '', notes: '' };

export default function NoteholdersPage() {
  const { currentCompany } = useCompany();
  const [noteholders, setNoteholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const { handleExportCSV, handleImportCSV, exporting, importing } = useExportImport({
    exportUrl: '/api/noteholders/export/csv',
    importUrl: '/api/noteholders/import/csv',
    companyId: currentCompany?.id,
    onImportSuccess: () => fetchNoteholders(),
    entityName: 'noteholders'
  });

  useEffect(() => {
    if (currentCompany) fetchNoteholders();
  }, [currentCompany]);

  const fetchNoteholders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/noteholders`, { params: { company_id: currentCompany.id }, withCredentials: true });
      setNoteholders(res.data);
    } catch { toast.error('Failed to load noteholders'); }
    finally { setLoading(false); }
  };

  const handleAdd = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/noteholders`, {
        company_id: currentCompany.id,
        name: form.name,
        total_invested: form.total_invested ? parseFloat(form.total_invested) : null,
        country: form.country || null,
        note_due_date: form.note_due_date || null,
        notes: form.notes || null
      }, { withCredentials: true });
      toast.success('Noteholder added');
      setIsAddOpen(false);
      setForm(EMPTY);
      fetchNoteholders();
    } catch { toast.error('Failed to add'); }
    finally { setSubmitting(false); }
  };

  const handleEdit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/noteholders/${selected.id}`, {
        name: form.name,
        total_invested: form.total_invested ? parseFloat(form.total_invested) : null,
        country: form.country || null,
        note_due_date: form.note_due_date || null,
        notes: form.notes || null
      }, { withCredentials: true });
      toast.success('Noteholder updated');
      setIsEditOpen(false);
      setSelected(null);
      setForm(EMPTY);
      fetchNoteholders();
    } catch { toast.error('Failed to update'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/api/noteholders/${deleteTarget.id}`, { withCredentials: true });
      toast.success('Noteholder deleted');
      setDeleteTarget(null);
      fetchNoteholders();
    } catch { toast.error('Failed to delete'); }
  };

  const openEdit = (nh) => {
    setSelected(nh);
    setForm({ name: nh.name, total_invested: nh.total_invested?.toString() || '', country: nh.country || '', note_due_date: nh.note_due_date || '', notes: nh.notes || '' });
    setIsEditOpen(true);
  };

  const formatCurrency = (v) => v != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v) : '-';
  const totalInvested = noteholders.reduce((s, n) => s + (n.total_invested || 0), 0);

  if (!currentCompany) return <div className="flex items-center justify-center h-64"><p className="text-gray-500">Please select a company</p></div>;

  const nhForm = (
    <div className="space-y-4 py-2">
      <div className="space-y-2"><Label>Client Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" data-testid="noteholder-name-input" /></div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2"><Label>Total Invested ($)</Label><Input type="number" step="0.01" value={form.total_invested} onChange={(e) => setForm({ ...form, total_invested: e.target.value })} placeholder="0.00" data-testid="noteholder-invested-input" /></div>
        <div className="space-y-2"><Label>Country</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="e.g. United States" data-testid="noteholder-country-input" /></div>
      </div>
      <div className="space-y-2"><Label>Note Due Date</Label><Input type="date" value={form.note_due_date} onChange={(e) => setForm({ ...form, note_due_date: e.target.value })} data-testid="noteholder-date-input" /></div>
      <div className="space-y-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Additional notes..." /></div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="noteholders-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Noteholders</h1>
          <p className="text-gray-500 mt-1">Manage noteholder records</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportImportButtons onExportCSV={handleExportCSV} onImportCSV={handleImportCSV} exporting={exporting} importing={importing} testIdPrefix="noteholders" />
          <Button onClick={() => { setForm(EMPTY); setIsAddOpen(true); }} data-testid="add-noteholder-btn"><Plus className="w-4 h-4 mr-2" />Add Noteholder</Button>
        </div>
      </div>

      {/* Summary */}
      {noteholders.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-sm text-gray-500 uppercase tracking-wide">Total Noteholders</p>
            <p className="text-2xl font-semibold mt-1">{noteholders.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-sm text-gray-500 uppercase tracking-wide">Total Invested</p>
            <p className="text-2xl font-semibold mt-1">{formatCurrency(totalInvested)}</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : noteholders.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No noteholders yet</h3>
          <p className="text-gray-500 mb-4">Add your first noteholder</p>
          <Button onClick={() => { setForm(EMPTY); setIsAddOpen(true); }}><Plus className="w-4 h-4 mr-2" />Add Noteholder</Button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client Name</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Invested</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Country</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Note Due Date</th>
                <th className="px-4 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {noteholders.map((nh) => (
                <tr key={nh.id} className="hover:bg-gray-50" data-testid={`noteholder-row-${nh.id}`}>
                  <td className="px-4 py-3 font-medium text-gray-900">{nh.name}</td>
                  <td className="px-4 py-3 text-right font-medium text-gray-900">{formatCurrency(nh.total_invested)}</td>
                  <td className="px-4 py-3">{nh.country ? <span className="text-sm text-gray-600 flex items-center gap-1"><Globe className="w-3.5 h-3.5" />{nh.country}</span> : <span className="text-gray-400 text-sm">-</span>}</td>
                  <td className="px-4 py-3">{nh.note_due_date ? <span className="text-sm text-gray-600 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{nh.note_due_date}</span> : <span className="text-gray-400 text-sm">-</span>}</td>
                  <td className="px-4 py-3">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(nh)}><Pencil className="w-4 h-4 mr-2" />Edit</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setDeleteTarget(nh)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent data-testid="add-noteholder-dialog"><DialogHeader><DialogTitle>Add Noteholder</DialogTitle><DialogDescription>Add a new noteholder record.</DialogDescription></DialogHeader>{nhForm}<DialogFooter><Button variant="outline" onClick={() => setIsAddOpen(false)}>Cancel</Button><Button onClick={handleAdd} disabled={submitting} data-testid="save-noteholder-btn">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}</Button></DialogFooter></DialogContent>
      </Dialog>
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent><DialogHeader><DialogTitle>Edit Noteholder</DialogTitle></DialogHeader>{nhForm}<DialogFooter><Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button><Button onClick={handleEdit} disabled={submitting}>{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</Button></DialogFooter></DialogContent>
      </Dialog>
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete Noteholder</AlertDialogTitle><AlertDialogDescription>Delete "{deleteTarget?.name}"?</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
